/**
 * FieldMetaEditor — F12.A2 T12.A2.7 + F12.C T12.C.4.
 *
 * Edycja właściwości pola modułu: showInList, isUnique, defaultValue.
 * Dla pól typu `relation` dodatkowo: cel (kartoteka własna `relationModuleId` ALBO kartoteka
 * systemowa `relationSystemModuleSlug` — wzajemnie wykluczone), relationDisplayField,
 * relationMultiple.
 * Renderowany w prawym panelu buildera poniżej ValidationRulesEditor.
 *
 * Backend: PUT `/api/modules/:moduleId/fields/:fieldId`
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, GitBranch, Link2, Plus, Settings2, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import type { FieldOption } from '@/components/modules/SelectOptionsEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { REFERENCE_TARGET_OPTIONS } from '@/lib/system-entity-adapters';

/**
 * Kartoteki systemowe dostępne jako cel relacji kartoteki własnej — 5 kartotek płaskich
 * (bez dokumentów: picker formularza obsługuje tylko kartoteki z adapterem). Mirror
 * `KNOWN_KARTOTEKA_SLUGS` po stronie API.
 */
const SYSTEM_RELATION_TARGETS = REFERENCE_TARGET_OPTIONS.filter((o) => o.value !== 'documents');

/** Wartość listy „Kartoteka docelowa" — jeden select na oba rodzaje celu. */
function encodeTarget(moduleId: string, systemSlug: string): string {
  if (systemSlug) return `system:${systemSlug}`;
  if (moduleId) return `module:${moduleId}`;
  return '';
}

/** Kartoteka modułu jako cel relacji; `sourceTemplateModuleId` ≠ null = zainstalowana przez ITE. */
interface RelationTargetModule {
  id: string;
  name: string;
  slug: string;
  sourceTemplateModuleId?: string | null;
}

interface FieldMetaEditorProps {
  moduleId: string;
  fieldId: string;
  fieldSlug: string;
  fieldType: string;
  initialShowInList: boolean;
  initialIsUnique: boolean;
  initialDefaultValue: string | null;
  /** Tylko dla fieldType='relation' */
  initialRelationModuleId?: string | null;
  initialRelationSystemModuleSlug?: string | null;
  initialRelationDisplayField?: string | null;
  initialRelationMultiple?: boolean;
  /** T12.D.5 — pola zależne (tylko select/multiselect) */
  initialDependsOnFieldSlug?: string | null;
  initialDependsOnOptionMap?: Record<string, string[]> | null;
  /** Opcje tego pola (do budowy macierzy uzależnienia) */
  currentFieldOptions?: FieldOption[];
  /** Inne pola select/multiselect w module (potencjalni rodzice) */
  siblingSelectFields?: Array<{ id: string; slug: string; name: string; options?: FieldOption[] }>;
  /** T12.D.6 — cascade defaults (tylko relation) */
  initialCascadeFromMap?: Record<string, string> | null;
  /** Wszystkie inne pola modułu (do selecta "Wypełnij pole") */
  allSiblingFields?: Array<{ id: string; slug: string; name: string }>;
}

interface MetaState {
  showInList: boolean;
  isUnique: boolean;
  defaultValue: string;
  relationModuleId: string;
  relationSystemModuleSlug: string;
  relationDisplayField: string;
  relationMultiple: boolean;
  dependsOnFieldSlug: string;
  dependsOnOptionMap: Record<string, string[]>;
  cascadeFromMap: Record<string, string>;
}

export function FieldMetaEditor({
  moduleId,
  fieldId,
  fieldSlug,
  fieldType,
  initialShowInList,
  initialIsUnique,
  initialDefaultValue,
  initialRelationModuleId,
  initialRelationSystemModuleSlug,
  initialRelationDisplayField,
  initialRelationMultiple,
  initialDependsOnFieldSlug,
  initialDependsOnOptionMap,
  currentFieldOptions,
  siblingSelectFields,
  initialCascadeFromMap,
  allSiblingFields,
}: FieldMetaEditorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isRelation = fieldType === 'relation';
  const isSelectLike = fieldType === 'select' || fieldType === 'multiselect';

  const [meta, setMeta] = useState<MetaState>({
    showInList: initialShowInList,
    isUnique: initialIsUnique,
    defaultValue: initialDefaultValue ?? '',
    relationModuleId: initialRelationModuleId ?? '',
    relationSystemModuleSlug: initialRelationSystemModuleSlug ?? '',
    relationDisplayField: initialRelationDisplayField ?? 'record_name',
    relationMultiple: initialRelationMultiple ?? false,
    dependsOnFieldSlug: initialDependsOnFieldSlug ?? '',
    dependsOnOptionMap: initialDependsOnOptionMap ?? {},
    cascadeFromMap: initialCascadeFromMap ?? {},
  });

  // Reset gdy zmieni się pole (user klika inne pole w builderze)
  useEffect(() => {
    setMeta({
      showInList: initialShowInList,
      isUnique: initialIsUnique,
      defaultValue: initialDefaultValue ?? '',
      relationModuleId: initialRelationModuleId ?? '',
      relationSystemModuleSlug: initialRelationSystemModuleSlug ?? '',
      relationDisplayField: initialRelationDisplayField ?? 'record_name',
      relationMultiple: initialRelationMultiple ?? false,
      dependsOnFieldSlug: initialDependsOnFieldSlug ?? '',
      dependsOnOptionMap: initialDependsOnOptionMap ?? {},
      cascadeFromMap: initialCascadeFromMap ?? {},
    });
  }, [
    fieldId,
    initialShowInList,
    initialIsUnique,
    initialDefaultValue,
    initialRelationModuleId,
    initialRelationSystemModuleSlug,
    initialRelationDisplayField,
    initialRelationMultiple,
    initialDependsOnFieldSlug,
    initialDependsOnOptionMap,
    initialCascadeFromMap,
  ]);

  // Lista modułów dla selecta (tylko gdy fieldType = 'relation')
  const { data: modulesData } = useQuery<{ modules: RelationTargetModule[] }>({
    // Własny klucz: pod samym ['modules'] inne ekrany trzymają TABLICĘ, a tu kształt `{ modules }`
    // — wspólny wpis cache dawał pustą listę celów, zależnie od tego, co wczytało się pierwsze.
    queryKey: ['modules', 'relation-targets'],
    queryFn: async () => {
      const res = await api.get<{ modules: RelationTargetModule[] }>('/modules');
      return res.data;
    },
    enabled: isRelation,
    staleTime: 60_000,
  });

  // T12.D.6 — Pola modułu docelowego (dla sekcji cascade)
  const { data: targetModuleData } = useQuery<{
    module: { fields: Array<{ id: string; slug: string; name: string }> };
  }>({
    queryKey: ['modules', meta.relationModuleId],
    queryFn: async () => {
      const res = await api.get(`/modules/${meta.relationModuleId}`);
      return res.data;
    },
    enabled: isRelation && !!meta.relationModuleId,
    staleTime: 60_000,
  });

  const hasChanges =
    meta.showInList !== initialShowInList ||
    meta.isUnique !== initialIsUnique ||
    meta.defaultValue !== (initialDefaultValue ?? '') ||
    (isRelation &&
      (meta.relationModuleId !== (initialRelationModuleId ?? '') ||
        meta.relationSystemModuleSlug !== (initialRelationSystemModuleSlug ?? '') ||
        meta.relationDisplayField !== (initialRelationDisplayField ?? 'record_name') ||
        meta.relationMultiple !== (initialRelationMultiple ?? false) ||
        JSON.stringify(meta.cascadeFromMap) !== JSON.stringify(initialCascadeFromMap ?? {}))) ||
    (isSelectLike &&
      (meta.dependsOnFieldSlug !== (initialDependsOnFieldSlug ?? '') ||
        JSON.stringify(meta.dependsOnOptionMap) !==
          JSON.stringify(initialDependsOnOptionMap ?? {})));

  const saveMutation = useMutation({
    mutationFn: async (s: MetaState) => {
      const body: Record<string, unknown> = {
        showInList: s.showInList,
        isUnique: s.isUnique,
        defaultValue: s.defaultValue.trim() === '' ? null : s.defaultValue.trim(),
      };
      if (isRelation) {
        body.relationModuleId = s.relationModuleId || null;
        body.relationSystemModuleSlug = s.relationSystemModuleSlug || null;
        body.relationDisplayField = s.relationDisplayField.trim() || 'record_name';
        body.relationMultiple = s.relationMultiple;
        body.cascadeFromMap = s.relationSystemModuleSlug ? {} : s.cascadeFromMap;
      }
      if (isSelectLike) {
        body.dependsOnFieldSlug = s.dependsOnFieldSlug || null;
        body.dependsOnOptionMap = s.dependsOnFieldSlug ? s.dependsOnOptionMap : {};
      }
      const res = await api.put(`/modules/${moduleId}/fields/${fieldId}`, body);
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.fieldMeta.toasts.saved') });
      queryClient.invalidateQueries({ queryKey: ['modules', moduleId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.fieldMeta.toasts.saveError') });
    },
  });

  return (
    <div
      className="mt-4 border-t border-border-primary pt-4"
      data-testid={`field-meta-editor-${fieldSlug}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <Settings2 className="h-4 w-4" />
          {t('tenant.modules.fieldMeta.title')}
        </h4>
        {hasChanges && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate(meta)}
            loading={saveMutation.isPending}
            disabled={saveMutation.isPending}
            data-testid={`field-meta-editor-${fieldSlug}-save`}
          >
            {t('tenant.modules.fieldMeta.save')}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={meta.showInList}
            onChange={(e) => setMeta((prev) => ({ ...prev, showInList: e.target.checked }))}
            data-testid={`field-meta-editor-${fieldSlug}-show-in-list`}
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
          />
          <span className="text-text-secondary">{t('tenant.modules.fieldMeta.showInList')}</span>
        </label>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={meta.isUnique}
            onChange={(e) => setMeta((prev) => ({ ...prev, isUnique: e.target.checked }))}
            data-testid={`field-meta-editor-${fieldSlug}-is-unique`}
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
          />
          <span className="text-text-secondary">{t('tenant.modules.fieldMeta.isUnique')}</span>
        </label>

        <div>
          <label
            htmlFor={`fm-${fieldSlug}-default`}
            className="mb-1 block text-xs text-text-secondary"
          >
            {t('tenant.modules.fieldMeta.defaultValue')}
          </label>
          <Input
            id={`fm-${fieldSlug}-default`}
            type="text"
            value={meta.defaultValue}
            onChange={(e) => setMeta((prev) => ({ ...prev, defaultValue: e.target.value }))}
            placeholder={t('tenant.modules.fieldMeta.defaultValuePlaceholder')}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder-text-muted focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
            data-testid={`field-meta-editor-${fieldSlug}-default-value`}
          />
        </div>
      </div>

      {/* ── Uzależnienie opcji (tylko dla select / multiselect) ────────────── */}
      {isSelectLike && siblingSelectFields && siblingSelectFields.length > 0 && (
        <div
          className="mt-4 border-t border-border-primary pt-3 space-y-3"
          data-testid={`field-meta-editor-${fieldSlug}-depends-config`}
        >
          <h5 className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
            <GitBranch className="h-3.5 w-3.5" />
            {t('tenant.modules.fieldMeta.depends.title')}
          </h5>

          <div>
            <label
              htmlFor={`fm-${fieldSlug}-depends-on`}
              className="mb-1 block text-xs text-text-secondary"
            >
              {t('tenant.modules.fieldMeta.depends.parentField')}
            </label>
            <Dropdown
              id={`fm-${fieldSlug}-depends-on`}
              value={meta.dependsOnFieldSlug}
              onChange={(slug) => {
                setMeta((prev) => ({
                  ...prev,
                  dependsOnFieldSlug: slug,
                  dependsOnOptionMap: slug ? prev.dependsOnOptionMap : {},
                }));
              }}
              data-testid={`field-meta-editor-${fieldSlug}-depends-on`}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary"
              options={[
                { value: '', label: t('tenant.modules.fieldMeta.depends.noParent') },
                ...siblingSelectFields.map((f) => ({ value: f.slug, label: f.name })),
              ]}
            />
          </div>

          {/* Macierz: parent option → dozwolone child options */}
          {meta.dependsOnFieldSlug &&
            (() => {
              const parentField = siblingSelectFields.find(
                (f) => f.slug === meta.dependsOnFieldSlug,
              );
              const parentOptions = parentField?.options ?? [];
              const childOptions = currentFieldOptions ?? [];
              if (parentOptions.length === 0 || childOptions.length === 0) {
                return (
                  <p className="text-xs text-text-muted">
                    {t('tenant.modules.fieldMeta.depends.noOptions')}
                  </p>
                );
              }
              return (
                <div className="space-y-2">
                  <p className="text-xs text-text-muted">
                    {t('tenant.modules.fieldMeta.depends.matrixHint')}
                  </p>
                  {parentOptions.map((pOpt) => {
                    const allowed = meta.dependsOnOptionMap[pOpt.value] ?? [];
                    return (
                      <div
                        key={pOpt.value}
                        className="rounded-md border border-border-primary p-2"
                        data-testid={`depends-row-${pOpt.value}`}
                      >
                        <p className="mb-1.5 text-xs font-medium text-text-primary">{pOpt.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {childOptions.map((cOpt) => (
                            <label
                              key={cOpt.value}
                              className="flex items-center gap-1 text-xs cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={allowed.includes(cOpt.value)}
                                onChange={(e) => {
                                  const newAllowed = e.target.checked
                                    ? [...allowed, cOpt.value]
                                    : allowed.filter((v) => v !== cOpt.value);
                                  setMeta((prev) => ({
                                    ...prev,
                                    dependsOnOptionMap: {
                                      ...prev.dependsOnOptionMap,
                                      [pOpt.value]: newAllowed,
                                    },
                                  }));
                                }}
                                className="h-3.5 w-3.5 rounded border-border-primary text-accent-600 focus:ring-accent-500"
                              />
                              <span className="text-text-secondary">{cOpt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </div>
      )}

      {/* ── Konfiguracja relacji (tylko dla fieldType='relation') ──────────── */}
      {isRelation && (
        <div
          className="mt-4 border-t border-border-primary pt-3 space-y-3"
          data-testid={`field-meta-editor-${fieldSlug}-relation-config`}
        >
          <h5 className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
            <Link2 className="h-3.5 w-3.5" />
            {t('tenant.modules.fieldMeta.relation.title')}
          </h5>

          <div>
            <label
              htmlFor={`fm-${fieldSlug}-rel-module`}
              className="mb-1 block text-xs text-text-secondary"
            >
              {t('tenant.modules.fieldMeta.relation.targetModule')}
            </label>
            <Dropdown
              id={`fm-${fieldSlug}-rel-module`}
              value={encodeTarget(meta.relationModuleId, meta.relationSystemModuleSlug)}
              onChange={(v) => {
                const isSystem = v.startsWith('system:');
                setMeta((prev) => ({
                  ...prev,
                  relationModuleId: v.startsWith('module:') ? v.slice('module:'.length) : '',
                  relationSystemModuleSlug: isSystem ? v.slice('system:'.length) : '',
                  cascadeFromMap: isSystem ? {} : prev.cascadeFromMap,
                }));
              }}
              data-testid={`field-meta-editor-${fieldSlug}-relation-module`}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary"
              options={[{ value: '', label: t('tenant.modules.fieldMeta.relation.selectModule') }]}
              groups={[
                // Kartoteki zainstalowane przez ITE (szablon huba) są dla tenanta systemowe;
                // do „własnych" trafiają tylko utworzone przez tenanta.
                {
                  label: t('tenant.modules.fieldMeta.relation.groupSystem'),
                  options: [
                    ...SYSTEM_RELATION_TARGETS.map((o) => ({
                      value: `system:${o.value}`,
                      label: t(o.labelKey),
                    })),
                    ...(modulesData?.modules ?? [])
                      .filter((m) => m.sourceTemplateModuleId != null)
                      .map((m) => ({ value: `module:${m.id}`, label: m.name })),
                  ],
                },
                {
                  label: t('tenant.modules.fieldMeta.relation.groupCustom'),
                  options: (modulesData?.modules ?? [])
                    .filter((m) => m.sourceTemplateModuleId == null)
                    .map((m) => ({ value: `module:${m.id}`, label: m.name })),
                },
              ]}
            />
          </div>

          {/* Pole wyświetlane dotyczy tylko kartoteki własnej — etykietę rekordu kartoteki
              systemowej wyznacza jej adapter (np. nazwa kontrahenta). */}
          {!meta.relationSystemModuleSlug && (
            <div>
              <label
                htmlFor={`fm-${fieldSlug}-rel-display`}
                className="mb-1 block text-xs text-text-secondary"
              >
                {t('tenant.modules.fieldMeta.relation.displayField')}
              </label>
              <Input
                id={`fm-${fieldSlug}-rel-display`}
                type="text"
                value={meta.relationDisplayField}
                onChange={(e) =>
                  setMeta((prev) => ({ ...prev, relationDisplayField: e.target.value }))
                }
                placeholder="record_name"
                data-testid={`field-meta-editor-${fieldSlug}-relation-display`}
                className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder-text-muted focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={meta.relationMultiple}
              onChange={(e) => setMeta((prev) => ({ ...prev, relationMultiple: e.target.checked }))}
              data-testid={`field-meta-editor-${fieldSlug}-relation-multiple`}
              className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
            />
            <span className="text-text-secondary">
              {t('tenant.modules.fieldMeta.relation.multiple')}
            </span>
          </label>

          {/* ── Cascade defaults ──────────────────────────────────────────── */}
          <div
            className="border-t border-border-primary pt-3 space-y-2"
            data-testid={`field-meta-editor-${fieldSlug}-cascade-config`}
          >
            <h6 className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
              <ArrowRight className="h-3.5 w-3.5" />
              {t('tenant.modules.fieldMeta.cascade.title')}
            </h6>

            {meta.relationSystemModuleSlug ? (
              <p className="text-xs text-text-muted">
                {t('tenant.modules.fieldMeta.cascade.systemTarget')}
              </p>
            ) : !meta.relationModuleId ? (
              <p className="text-xs text-text-muted">
                {t('tenant.modules.fieldMeta.cascade.noTargetModule')}
              </p>
            ) : (
              (() => {
                const targetFields = targetModuleData?.module.fields ?? [];
                const localFields = allSiblingFields ?? [];

                if (targetFields.length === 0) {
                  return (
                    <p className="text-xs text-text-muted">
                      {t('tenant.modules.fieldMeta.cascade.noFields')}
                    </p>
                  );
                }

                const mappingEntries = Object.entries(meta.cascadeFromMap);

                return (
                  <div className="space-y-1.5">
                    <p className="text-xs text-text-muted">
                      {t('tenant.modules.fieldMeta.cascade.hint')}
                    </p>

                    {mappingEntries.map(([targetSlug, localSlug], idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-1.5"
                        data-testid={`cascade-row-${idx}`}
                      >
                        {/* Pole z modułu docelowego */}
                        <Dropdown
                          value={targetSlug}
                          onChange={(newTargetSlug) => {
                            setMeta((prev) => {
                              const entries = Object.entries(prev.cascadeFromMap);
                              entries[idx] = [newTargetSlug, localSlug];
                              return {
                                ...prev,
                                cascadeFromMap: Object.fromEntries(entries),
                              };
                            });
                          }}
                          className="flex-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary"
                          options={[
                            { value: '', label: t('tenant.modules.fieldMeta.cascade.selectField') },
                            ...targetFields.map((f) => ({ value: f.slug, label: f.name })),
                          ]}
                        />

                        <ArrowRight className="h-3 w-3 shrink-0 text-text-muted" />

                        {/* Pole lokalne (bieżący moduł) */}
                        <Dropdown
                          value={localSlug}
                          onChange={(newLocalSlug) => {
                            setMeta((prev) => {
                              const entries = Object.entries(prev.cascadeFromMap);
                              entries[idx] = [targetSlug, newLocalSlug];
                              return {
                                ...prev,
                                cascadeFromMap: Object.fromEntries(entries),
                              };
                            });
                          }}
                          className="flex-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary"
                          options={[
                            { value: '', label: t('tenant.modules.fieldMeta.cascade.selectField') },
                            ...localFields.map((f) => ({ value: f.slug, label: f.name })),
                          ]}
                        />

                        {/* Usuń mapowanie */}
                        <button
                          type="button"
                          onClick={() => {
                            setMeta((prev) => {
                              const entries = Object.entries(prev.cascadeFromMap);
                              entries.splice(idx, 1);
                              return {
                                ...prev,
                                cascadeFromMap: Object.fromEntries(entries),
                              };
                            });
                          }}
                          className="shrink-0 rounded p-0.5 text-text-muted hover:text-danger-600 hover:bg-danger-50"
                          data-testid={`cascade-remove-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}

                    {/* Dodaj mapowanie */}
                    <button
                      type="button"
                      onClick={() => {
                        setMeta((prev) => ({
                          ...prev,
                          cascadeFromMap: { ...prev.cascadeFromMap, '': '' },
                        }));
                      }}
                      className="flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
                      data-testid={`field-meta-editor-${fieldSlug}-cascade-add`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t('tenant.modules.fieldMeta.cascade.addMapping')}
                    </button>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}
